"use strict";
/**
 * Language detection utility for detecting user's preferred language
 * from cookies and Accept-Language headers.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.detectLanguage = detectLanguage;
/**
 * Detects the user's preferred language from cookies and Accept-Language header.
 *
 * Detection priority:
 * 1. Language preference in cookies (i18next, locale, language, lang)
 * 2. Accept-Language header with quality value support
 * 3. Fallback language
 *
 * @param options - Language detection options
 * @returns The detected language code
 *
 * @example
 * ```typescript
 * const language = detectLanguage({
 *   supportedLanguages: ['en', 'fr', 'es'],
 *   fallbackLanguage: 'en',
 *   cookies: req.cookies,
 *   acceptLanguageHeader: req.headers['accept-language']
 * });
 * ```
 */
function detectLanguage(options) {
    const { supportedLanguages, fallbackLanguage, cookies, acceptLanguageHeader, } = options;
    const allSupportedLanguages = [fallbackLanguage, ...supportedLanguages];
    // First, check if there's a language preference in cookies
    // Common cookie names: 'i18next', 'locale', 'language', 'lang'
    if (cookies) {
        const cookieLanguage = cookies.i18next || cookies.locale || cookies.language || cookies.lang;
        if (cookieLanguage && allSupportedLanguages.includes(cookieLanguage)) {
            return cookieLanguage;
        }
    }
    // Second, parse the Accept-Language header
    if (acceptLanguageHeader) {
        // Format: "en-US,en;q=0.9,fr;q=0.8"
        const languages = acceptLanguageHeader
            .split(',')
            .map((lang) => {
            const [locale, qValue] = lang.trim().split(';q=');
            const quality = qValue ? parseFloat(qValue) : 1.0;
            // Extract the main language code (e.g., 'en' from 'en-US')
            const code = locale.split('-')[0].toLowerCase();
            return { code, quality };
        })
            .sort((a, b) => b.quality - a.quality);
        // Find the first supported language
        for (const lang of languages) {
            if (allSupportedLanguages.includes(lang.code)) {
                return lang.code;
            }
        }
    }
    return fallbackLanguage;
}
